/**
 * TEBHOS WebSocket Client
 *
 * GATEWAY-CUTOVER (2026-07-16): Erlang opcode-gateway'e (:9100, GatewayV2Socket,
 * tam Discord {op,t,d,s} protokolü) TEK ve KOŞULSUZ transport. Eski tokio :9000
 * (düz {type,data} zarfı) yolu FE'den SÖKÜLDÜ — GatewayV2Flag.ts silindi, bu
 * dosya artık hiçbir koşulda :9000'e bağlanmıyor. Backend tarafında da tokio
 * ws-gateway KALINTISIZ SİLİNDİ (GATEWAY-CUTOVER 2026-07-17: crate + compose
 * `ws` servisi + nginx upstream'i söküldü) — :9000 artık hiçbir şey dinlemiyor.
 *
 * connect()/disconnect() artık doğrudan GatewayV2Socket'i sarmalar; auth/
 * presence akışı sunucu-güdümlü HELLO→IDENTIFY(op2,token+presence)→READY'dir
 * (eski :9000'in client-taraf {type:'auth'}+{type:'presence:join'} gönderimi
 * YOK — Erlang bunu beklemiyor/işlemiyor).
 */

import { reaction } from "mobx";
import { gatewayDispatcher } from "@app/features/gateway/utils/GatewayDispatcher";
import { GatewayV2Socket } from "@app/features/gateway/utils/GatewayV2Socket";
// Erlang :9100 — seçili sunucu op14 {active,sync} aboneliği (Kaynak
// GatewayConnection.setupSelectedGuildSync paritesi).
import SelectedGuildStore from "@app/features/navigation/state/SelectedGuildStore";
// Erlang :9100 — KENDİ presence'ını Kaynak-stili LocalPresence ile TAM
// zenginlikte (status+afk+mobile+custom_status) yayınlayan köprü.
import { installLocalPresenceBroadcast } from "@app/features/presence/state/LocalPresenceBroadcast";
// Erlang :9100 — üye-liste presence aboneliği (op14) köprüsü.
import { MemberPresenceSubscriptionStore } from "@app/features/presence/state/MemberPresenceSubscription";
import { loadBootStatusHint } from "@app/features/presence/state/PresenceStore";
// Kök: lib/config/RuntimeConfig.ts (tek kaynak — duplike edilmedi). wsUrl artık
// Erlang :9100 gösterir (bkz. RuntimeConfig.ts başlığındaki GATEWAY-CUTOVER notu).
import { RuntimeConfig } from "@/lib/config/RuntimeConfig";
// PARİTE-DENETİM FIX (2026-07-18) — Kaynak GatewaySocket.handleAuthFailure
// (GatewaySocket.ts:1172-1179) paritesi: 4004'te güvenli-sıfırlama
// (clearExcept — taslaklar korunur) + oturum kapatma.
import AppStorage, { PRESERVED_RESET_STORAGE_KEYS } from "@app/features/platform/state/PersistentStorage";
// AÇILIŞ SIRASI (21 Eyl 2026) — token kasası: soket, kimlik REST'ini beklemeden
// saklı hesaptan token+id ipucunu okur (bkz. connect()). `lib/api/client.ts`
// zaten bu modülü import ettiğinden yeni bir modül döngüsü doğmaz.
import { AccountManagerStore } from "@app/features/auth/state/AccountManagerStore";
import { apiFetch, clearTokens, getAccessToken } from "./client";

type EventHandler = (data: any) => void;

export class TebhosWebSocket {
  private handlers: Map<string, Set<EventHandler>> = new Map();
  private profileId: string = "";
  private profileName: string = "";

  // Erlang :9100 adaptörü — connect() sonrası tek örnek olarak tutulur.
  private v2: GatewayV2Socket | null = null;

  // Erlang :9100 — LocalPresenceBroadcast disposer; disconnect'te sökülür.
  private localPresenceDispose: (() => void) | null = null;

  // Kaynak GatewayConnection.createGatewaySocket 1-1 — ağ/görünürlük dinleyicileri
  // (window 'online', document 'visibilitychange', window 'pageshow'):
  // uyku/arka plan dönüşünde soketi ANINDA yoklayıp bayatsa resume-window
  // içinde RESUME ile döndürür (eskiden yoktu; zombi soket ancak heartbeat-ack
  // zaman aşımıyla fark ediliyor, session da 4009'la temizlenip her dönüşte
  // sıfırdan IDENTIFY ediliyordu).
  private onlineListener: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private pageshowListener: ((event: PageTransitionEvent) => void) | null = null;

  // Seçili-sunucu op14 sync durumu (Kaynak GatewayConnection paritesi:
  // syncedGuildSessions/pendingGuildSyncId). READY olmadan gönderilen op14
  // GatewayV2Socket'te sessizce düşer → ready-flush ŞART.
  private selectedGuildSyncDispose: (() => void) | null = null;
  private syncedGuilds: Set<string> = new Set();
  private pendingGuildSyncId: string | null = null;
  private gatewayReady = false;

  /**
   * Tek event'i üç yola dağıt: (1) merkezi gatewayDispatcher, (2) legacy
   * component handler'ları (`tebhosWs.on(eventName, handler)`), (3) wildcard.
   * GatewayV2Socket.onDispatch buradan AYNI yola girer — davranış tek.
   */
  private routeEvent(eventType: string, data: any): void {
    // Merkezi gateway dispatch — domain store'ları günceller
    gatewayDispatcher.dispatch(eventType, data);

    // Component-level handler'lar (ör. HomeSidebar `.on("dm:sidebar-update", ...)`)
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }

    // Wildcard handler
    const wildcard = this.handlers.get("*");
    if (wildcard) {
      wildcard.forEach((handler) => handler({ type: eventType, ...data }));
    }
  }

  /**
   * AÇILIŞ SIRASI (21 Eyl 2026) — kimlik artık ÖN KOŞUL DEĞİL.
   *
   * NEDEN: eskiden çağıran taraf (SocketProvider) `GET /users/@me` yanıtını
   * bekleyip id+ad veriyordu; soket bu yüzden REST'ten SONRA açılıyor, READY de
   * REST'ten sonra geliyordu (ölçüm: READY ~1100-1470ms, ilk REST ~907ms).
   * Kaynak `GatewayConnection.startSession(token)` 1-1: IDENTIFY yalnız TOKEN
   * ister; kullanıcı nesnesi READY yüküyle gelir. Argümanlar verilmezse saklı
   * hesap kaydından (AccountManagerStore — token zaten orada) okunur.
   *
   * `profileId` yalnız READY öncesi kendi-presence aynası için bir İPUCU'dur
   * (Kaynak `DesiredSession.userIdHint` karşılığı) ve READY'de yükteki gerçek
   * `user.id` ile değiştirilir.
   */
  connect(profileId?: string, profileName?: string) {
    if (this.v2) return; // zaten bir adaptör varsa yeniden oluşturma (reconnect'i adaptör yönetir)

    const url = RuntimeConfig.wsUrl;
    if (!url) {
      console.warn("[WS9100] NEXT_PUBLIC_WS_URL yok → bağlanılmıyor");
      return;
    }

    const token = getAccessToken() || "";
    if (!token) {
      // Kaynak startSession'ın "saklı token yok → bağlanma" dalı 1-1: tokensız
      // IDENTIFY kesin 4004 alır, soketi hiç açmanın anlamı yok.
      console.warn("[WS9100] oturum tokenı yok → bağlanılmıyor");
      return;
    }

    const account = AccountManagerStore.getCurrent();
    this.profileId = profileId ?? account?.id ?? "";
    this.profileName = profileName ?? account?.name ?? "";

    // IDENTIFY(op2) READY'den (ve UserSettings hidrasyonundan) ÖNCE gider →
    // son bilinen durum açılış ipucundan okunur; READY sonrası yetkili durum
    // UserSettings → LocalPresence → op3 ile zaten yeniden yayınlanır.
    const savedStatus = loadBootStatusHint();

    this.v2 = new GatewayV2Socket(
      { url, token, profileId: this.profileId, profileName: this.profileName, initialStatus: savedStatus },
      {
        onDispatch: (eventName, data) => this.routeEvent(eventName, data),
        onOpen: () => {
          console.log("[WS9100] connected (adaptör)");
        },
        onReady: (data) => {
          // AÇILIŞ SIRASI (21 Eyl 2026): kendi kimliğimizin YETKİLİ kaynağı
          // READY yüküdür. Bağlanırken kullanılan saklı-hesap ipucu (legacy/
          // sentinel id olabilir) burada gerçek `user.id` ile değiştirilir.
          const readyUserId = typeof (data as any)?.user?.id === "string" ? (data as any).user.id : null;
          if (readyUserId) this.profileId = readyUserId;
          // Yeni oturum → guild-sync durumu sıfırlanır ve bekleyen/seçili
          // sunucu için op14 {active,sync} floşlanır (Kaynak
          // flushPendingGuildSync paritesi — GUILD_SYNC presences getirir).
          this.gatewayReady = true;
          this.syncedGuilds.clear();
          this.flushPendingGuildSync();
          // TEK-DEPO GÖÇÜ (22 Eyl 2026): kendi statüsünün aynasını burada elle
          // yazmak GEREKMİYOR — portlanmış READY handler'ı (Presence.
          // handleGatewayReady) kendi kimliğini LocalPresence durumuyla zaten
          // kaydeder; eski PresenceStore aynası da silindi.
          // PRESENCE-DONMASI FIX (2026-07-28): üye-listesi presence abonelikleri
          // (op14 {members:[…]}) gateway'de OTURUM ÖMÜRLÜ. Yeni READY = yeni
          // oturum → guild sürecindeki `member_subscriptions` boş; istemcinin
          // LRU kayıtları ise hâlâ "taze" olduğu için touchMember bir daha
          // tetiklenmiyor ve o istemcide üye listesi çevrimiçi/çevrimdışı
          // tablosu sessizce DONUYORDU. READY'de aboneliği yeniden bas.
          MemberPresenceSubscriptionStore.resyncAll();
          // socket-provider READY değil "connected" local-event'ini dinliyor.
          this.emitLocal("connected", {});
        },
        onClose: () => {
          this.gatewayReady = false;
          this.emitLocal("disconnected", {});
        },
        // PARİTE-DENETİM FIX (2026-07-18) — Kaynak handleAuthFailure
        // (GatewaySocket.ts:1172-1179) 1-1: kalıcı 4004'te istemci durumu
        // güvenli-sıfırlanır (AppStorage.clearExcept — TASLAKLAR KORUNUR)
        // ve oturum kapatılıp giriş sayfasına dönülür. Önceden bu callback
        // hiç kablolanmamıştı (socket sessizce ölü kalıyordu).
        onAuthFailure: () => {
          try {
            AppStorage.clearExcept(PRESERVED_RESET_STORAGE_KEYS);
          } catch {
            /* storage erişilemiyorsa yine de çıkışa düş */
          }
          // AÇILIŞ SIRASI (21 Eyl 2026) — BAYAT TOKEN SORUMLULUĞU BURAYA GEÇTİ.
          // Eskiden açılıştaki `GET /users/@me` 401 alınca `clearTokens()` ile
          // geçersiz hesabı kasadan düşürüyordu; o çağrı açılıştan kalktığı için
          // tek karar mercii gateway 4004'tür. Hesap kaydı silinmezse
          // /sign-in'e dönen sayfa aynı bayat token'la yeniden bağlanmayı dener
          // (bağlan → 4004 → yönlendir → bağlan… döngüsü).
          // `AppStorage.clearExcept` bu anahtara dokunmaz (ayrı kasa).
          try {
            clearTokens();
          } catch {
            /* kasa erişilemiyorsa yine de çıkışa düş */
          }
          this.disconnect();
          // AÇIK SAYFALARDAN ZORLA YÖNLENDİRME (22 Eyl 2026 düzeltmesi).
          // Yönlendirme koşulsuzdu; oysa soket TÜM rotaları saran sağlayıcıda
          // kuruluyor. Süresi dolmuş oturumu olan kullanıcı şifre sıfırlama
          // bağlantısına tıkladığında sayfa açılıyor, ~1 sn sonra 4004 geliyor
          // ve /sign-in'e atıyordu — TEK KULLANIMLIK token URL'den yok oluyordu.
          // Aynısı davet, hediye ve "ben değildim" bağlantılarında da oluyordu.
          // Eski 401 dalında bu kontrol vardı, açılış değişikliğiyle düştü.
          if (typeof window !== "undefined") {
            const PROTECTED_PREFIXES = ["/channels", "/account", "/settings", "/discovery", "/inventory"];
            // `pathname` her ortamda taşınmıyor (testlerde location taklit ediliyor);
            // okunamıyorsa korumalı saymak güvenli taraf — oturum geçersizse
            // kullanıcıyı giriş ekranına almak varsayılan davranış olmalı.
            const path = window.location?.pathname ?? "/channels";
            if (PROTECTED_PREFIXES.some((prefix) => path.startsWith(prefix))) {
              window.location.href = "/sign-in";
            }
          }
        },
      }
    );
    this.v2.connect();

    // Kaynak GatewayConnection.createGatewaySocket 1-1 — dinleyici kabloları.
    // isCurrent guard'ı: dinleyici, kurulduğu adaptör örneği hâlâ aktifken çalışır
    // (Kaynak `this.socket === socket && this.generation === generation` paritesi).
    const v2ForListeners = this.v2;
    const isCurrent = () => this.v2 === v2ForListeners;
    this.removeNetworkListeners();
    if (typeof window !== "undefined") {
      this.onlineListener = () => {
        if (!isCurrent()) return;
        v2ForListeners.handleNetworkStatusChange(true);
      };
      window.addEventListener("online", this.onlineListener);
      this.visibilityListener = () => {
        if (!isCurrent()) return;
        if (document.visibilityState !== "visible") return;
        v2ForListeners.probeAfterResume("visibilitychange", { accelerateReconnect: false });
      };
      document.addEventListener("visibilitychange", this.visibilityListener);
      this.pageshowListener = (event: PageTransitionEvent) => {
        if (!isCurrent()) return;
        if (event.persisted) {
          // bfcache'ten dönüş: soket kesin bayat — zorla taze bağlantı (Kaynak 1-1).
          v2ForListeners.forceReconnectFromResume("pageshow-bfcache");
          return;
        }
        v2ForListeners.probeAfterResume("pageshow", { accelerateReconnect: false });
      };
      window.addEventListener("pageshow", this.pageshowListener);
    }

    // Üye-liste presence aboneliği (Kaynak MemberPresenceSubscription →
    // socket.updateGuildSubscriptions 1-1).
    MemberPresenceSubscriptionStore.setSyncHandler({
      syncMembers: (guildId, members) => this.updateGuildSubscriptions(guildId, { members }),
      syncActive: (guildId, active) => this.updateGuildSubscriptions(guildId, { active }),
    });

    // Seçili sunucu → op14 {active:true, sync:true} (Kaynak
    // GatewayConnection.setupSelectedGuildSync 1-1): Erlang GUILD_SYNC
    // (members+presences) döner → üye-paneli online/offline gruplaması dolar.
    // Oturum başına guild başına bir kez (syncedGuilds); READY öncesi seçim
    // pendingGuildSyncId'de bekler, onReady floşlar.
    this.selectedGuildSyncDispose = reaction(
      () => ({
        guildId: SelectedGuildStore.selectedGuildId,
        nonce: SelectedGuildStore.selectionNonce,
      }),
      ({ guildId }) => {
        if (!guildId) {
          this.pendingGuildSyncId = null;
          return;
        }
        this.ensureGuildActiveAndSynced(guildId);
      },
      { fireImmediately: true }
    );

    // KENDİ presence yayını (Kaynak GatewayConnection paritesi): LocalPresence
    // presenceKey değiştikçe op3'ü TAM parametre setiyle (status+afk+mobile+
    // custom_status) gönderir.
    // Ayar yüzeyi burada ENJEKTE EDİLMEZ (bkz. LocalPresenceBroadcast doc):
    // tek sahip `UserSettings`tir. Kendi statüsünün aynası da elle yazılmaz —
    // Erlang gateway kendine PRESENCE_UPDATE göndermez ama portlanmış `Presence`
    // deposu kendi durumunu `LocalPresence` reaction'ıyla eşitler.
    this.localPresenceDispose = installLocalPresenceBroadcast({
      updatePresence: (status, afk, mobile, customStatus) => {
        this.v2?.updatePresence(status, afk, mobile, customStatus);
      },
    });
  }

  /**
   * op14 LAZY_REQUEST patch — tek guild için üye-listesi/aktiflik aboneliği.
   *
   * `member_list_channels`/`typing` alanları (2026-08-11): MemberSidebar'ın
   * kanal-kapsamlı üye-listesi penceresi (kaydırma aralıkları) bu uçtan
   * gönderilir. GatewayV2Socket.updateGuildSubscriptions zaten tam d-şeklini
   * taşıyordu; Erlang tarafı da guild_unified_subscriptions:
   * process_member_list_channels ile bu anahtarı işliyor — yalnız bu TEBHOS
   * sarmalayıcısının patch tipi dar kalmıştı.
   */
  updateGuildSubscriptions(
    guildId: string,
    patch: {
      active?: boolean;
      sync?: boolean;
      members?: string[];
      typing?: boolean;
      member_list_channels?: Record<string, Array<[number, number]>>;
    }
  ): void {
    if (!this.v2) return;
    this.v2.updateGuildSubscriptions({ [guildId]: patch });
  }

  /**
   * op8 REQUEST_GUILD_MEMBERS geçişi — GuildMembers store'un (features/member/
   * state/GuildMembers.ts) tek erişim noktası. READY-öncesi çağrı
   * GatewayV2Socket.requestGuildMembers içinde sessizce düşer (op15/op16
   * sarmalayıcılarıyla aynı desen).
   */
  requestGuildMembers(params: {
    guildId?: string;
    guildIds?: string[];
    query?: string;
    limit?: number;
    userIds?: string[];
    presences?: boolean;
    nonce?: string;
  }): void {
    this.v2?.requestGuildMembers(params);
  }

  /**
   * Kaynak GatewayConnection.ensureGuildActiveAndSynced 1-1 (TEBHOS yüzeyi):
   * READY değilse beklet; READY'yse oturum başına bir kez op14 gönder.
   */
  private ensureGuildActiveAndSynced(guildId: string): void {
    if (!this.v2 || !this.gatewayReady) {
      this.pendingGuildSyncId = guildId;
      return;
    }
    if (this.syncedGuilds.has(guildId)) {
      return;
    }
    this.updateGuildSubscriptions(guildId, { active: true, sync: true });
    this.syncedGuilds.add(guildId);
    this.pendingGuildSyncId = null;
  }

  /**
   * Kaynak GatewayConnection.markGuildSynced 1-1 (TEBHOS yüzeyi): GUILD_SYNC
   * geldiğinde sunucu bu oturum için eşitlenmiş sayılır → aynı oturumda ikinci
   * op14 {active,sync} gönderilmez. Oturum yoksa (READY öncesi) yok sayılır;
   * küme her yeni READY'de zaten sıfırlanır (oturum-anahtarlı kaydın eşdeğeri).
   * Çağıran: ReferenceRouter bağlamı (portlanmış GuildSync handler'ı).
   */
  markGuildSynced(guildId: string): void {
    if (!guildId || !this.sessionId) return;
    this.syncedGuilds.add(guildId);
    if (this.pendingGuildSyncId === guildId) {
      this.pendingGuildSyncId = null;
    }
  }

  /**
   * Kaynak GatewaySocket.setToken 1-1: sonraki IDENTIFY/RESUME yeni token'la
   * gider (AUTH_SESSION_CHANGE.new_token). Açık bağlantıya dokunmaz.
   */
  setToken(token: string): void {
    this.v2?.setToken(token);
  }

  /** Kaynak flushPendingGuildSync 1-1: bekleyen ya da seçili sunucuyu sync'le. */
  private flushPendingGuildSync(): void {
    const guildId = this.pendingGuildSyncId ?? SelectedGuildStore.selectedGuildId;
    if (!guildId) {
      return;
    }
    this.ensureGuildActiveAndSynced(guildId);
  }

  /**
   * op15 REQUEST_GUILD_COUNTS geçişi — GuildCount store'un (features/guild/
   * state/GuildCount.ts) tek erişim noktası. READY-öncesi çağrı GatewayV2Socket
   * içinde sessizce düşer (bkz. o dosyadaki yorum).
   */
  requestGuildCounts(guildIds: string[]): void {
    this.v2?.requestGuildCounts(guildIds);
  }

  /**
   * op16 REQUEST_CHANNEL_MEMBER_COUNTS geçişi — ChannelMemberCount store'un
   * (features/guild/state/ChannelMemberCount.ts) tek erişim noktası.
   */
  requestChannelMemberCounts(params: { guildId: string; channelIds: string[]; nonce?: string }): void {
    this.v2?.requestChannelMemberCounts(params);
  }

  /** Kaynak GatewayConnection.removeNetworkListeners 1-1. */
  private removeNetworkListeners(): void {
    if (typeof window === "undefined") return;
    if (this.onlineListener) {
      window.removeEventListener("online", this.onlineListener);
      this.onlineListener = null;
    }
    if (this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.pageshowListener) {
      window.removeEventListener("pageshow", this.pageshowListener);
      this.pageshowListener = null;
    }
  }

  /** Canlı gateway oturum kimliği; bağlantı/READY yoksa null. */
  get sessionId(): string | null {
    return this.v2?.getSessionId() ?? null;
  }

  disconnect() {
    if (!this.v2) return;
    this.v2.disconnect();
    this.v2 = null;
    // Kaynak cleanupSocket paritesi: dinleyiciler soketle birlikte sökülür.
    this.removeNetworkListeners();
    // Seçili-sunucu sync reaction'ını ve durumunu sök (Kaynak paritesi).
    this.selectedGuildSyncDispose?.();
    this.selectedGuildSyncDispose = null;
    this.syncedGuilds.clear();
    this.pendingGuildSyncId = null;
    this.gatewayReady = false;
    // Sync handler'ı sök — sonraki oturumda store yeniden client-only/no-op'a döner.
    MemberPresenceSubscriptionStore.setSyncHandler(null);
    // LocalPresence köprüsünü sök (installed guard'ı da sıfırlanır) → sonraki
    // oturumda temiz kurulum.
    this.localPresenceDispose?.();
    this.localPresenceDispose = null;
  }

  // ── Event System ──

  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off(event: string, handler?: EventHandler) {
    if (handler) {
      this.handlers.get(event)?.delete(handler);
    } else {
      // Remove all handlers for this event (Socket.IO compat)
      this.handlers.delete(event);
    }
  }

  private emitLocal(event: string, data: any) {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  /** Socket.IO compat: socket.emit("event", data) sends to server */
  emit(event: string, data?: any, _callback?: Function) {
    if (typeof data === "function") {
      // socket.emit("ping", callback) pattern
      this.send({ type: event });
      data(); // call the callback immediately for ping
      return;
    }
    this.send({ type: event, ...(data || {}) });
  }

  // ── Send ──

  /**
   * Düz {type,...} zarfı :9100'e (op-code protokolü) gönderilemez. Giden
   * aksiyonlar Discord op'larına eşlenir (Kaynak 1-1); op karşılığı olmayan
   * REST-güdümlü aksiyonlar no-op düşer. Ayrıntı: sendV2().
   */
  send(data: any) {
    this.sendV2(data);
  }

  /**
   * :9100 (Erlang) OUTBOUND op-eşleme — Kaynak FE gateway-send yolu 1-1.
   *
   * Discord modelinde "oda" yoktur: guild'ler READY'de otomatik-scope edilir,
   * üyeler op8/op14 ile, voice op4 ile, presence op3 ile istenir; typing ise
   * gateway'de DEĞİL REST'te (POST /channels/{id}/typing). op karşılığı olmayan
   * TEBHOS-özel relay'ler (reaction/pin/broadcast/dm-reaction/invite/call-end)
   * Discord'da REST-güdümlüdür → sunucu DISPATCH'i REST sonrası fan-out eder →
   * gateway'de no-op. (Uydurma op üretilmez.)
   *
   * DOĞRULANDI (2026-07-16 cutover denetimi): reaction/pin/dm-reaction için
   * backend zaten REST-sonrası MESSAGE_REACTION_ADD/REMOVE ve channel:pins-update
   * (CHANNEL_PINS_UPDATE) dispatch ediyor (Discord-model, authoritative) — bu
   * client-taraf WS-push'ları Socket.IO-döneminden kalan DUPLIKE bildirimdi,
   * no-op'a düşmeleri regresyon DEĞİL. `invite:send` (DM davet bildirimi) İSTİSNA:
   * Erlang backend'de karşılığı YOK (INVITE_RECEIVED hiç yayılmıyor — bkz.
   * handlers/index.ts dosya başı "wire adı uyuşmazlığı" notu) → bu no-op GERÇEK bir davranış
   * kaybı (alıcı realtime bildirim almaz, F5 sonrası REST ile görür); backend
   * işi, bu turun kapsamı dışı (rapora taşındı).
   */
  private sendV2(data: any) {
    const v2 = this.v2;
    if (!v2) return;
    switch (data?.type) {
      case "presence:update":
        // op3 — status zorunlu; afk/mobile/custom_status varsa taşınır
        // (kısmi-presence, Discord semantiği). Kaynak 1-1: LocalPresence
        // zaten bunları hesaplıyor — çağıran verirse buradan wire'a gider.
        if (typeof data.status === "string") {
          v2.updatePresence(data.status, data.afk, data.mobile, data.custom_status);
        }
        return;
      case "voice:join":
        // op4 — kanala katıl (channel_id=<id>). guild_id payload'da varsa taşınır,
        // yoksa null (DM voice). self_mute/self_deaf verilmemişse false.
        v2.updateVoiceState({
          guild_id: data.guild_id ?? data.server_id ?? null,
          channel_id: data.channel_id ?? null,
          self_mute: data.self_mute ?? false,
          self_deaf: data.self_deaf ?? false,
        });
        return;
      case "voice:leave":
        // op4 — kanaldan ayrıl (channel_id=null). Discord'da leave = null-channel op4.
        v2.updateVoiceState({
          guild_id: data.guild_id ?? data.server_id ?? null,
          channel_id: null,
          self_mute: false,
          self_deaf: false,
        });
        return;
      case "typing:start":
        // Discord/Kaynak: typing = REST POST /channels/{id}/typing (gateway op DEĞİL).
        // Kaynak TypingCommands.sendTyping ile birebir; hata sessizce yutulur.
        if (data.channel_id) {
          apiFetch(`/channels/${data.channel_id}/typing`, { method: "POST" }).catch(() => {
            /* typing best-effort — Kaynak de hatayı yutar */
          });
        }
        return;
      case "typing:stop":
        // Discord'da typing:stop YOK — indicator sunucuda TTL ile söner → no-op.
        return;
      case "joinRoom":
      case "leaveRoom":
        // TEBHOS oda = `server:{id}` (guild scope). Discord READY guild'leri
        // otomatik-scope eder → op gerekmez. Kanal-scope üye-listesi/typing
        // ayrıca op14 (updateGuildSubscriptions) / op8 (requestGuildMembers) ile
        // istenir; joinRoom bunu tetiklemiyor → no-op.
        return;
      case "ping":
        // :9100 adaptörü kendi heartbeat'ini (op1) yönetir → no-op.
        return;
      default:
        // reaction:update / pin:update / broadcast:message / dm:reaction-update /
        // invite:send / dm:call-end: Discord'da REST-güdümlü, gateway-op'u YOK.
        console.debug("[WS9100] giden mesaj op-eşleniği yok, no-op:", data?.type);
        return;
    }
  }

  get connected(): boolean {
    return this.v2?.connected ?? false;
  }

  /**
   * GuildCount/ChannelMemberCount store'larının (features/guild/state) "istek
   * hiç yanıtlanmayacak mı" kontrolü — Kaynak `GatewayConnection.socket`
   * truthy-check paritesi (adaptör örneği var mı; gerçek READY/isReady kapısı
   * GatewayV2Socket.requestGuildCounts/requestChannelMemberCounts İÇİNDE).
   */
  get hasGatewayAdapter(): boolean {
    return this.v2 != null;
  }

  /** Unique client identifier — uses profileId (analogous to Socket.IO socket.id) */
  get id(): string {
    return this.profileId;
  }

  // ── Room Management ──
  // Discord modelinde "oda" yok (guild'ler READY'de otomatik-scope) — bu
  // metodlar sendV2 "joinRoom"/"leaveRoom" case'i üzerinden bilinçli no-op'a
  // düşer (Kaynak paritesi). Çağıran taraflar (ör. NavigationSidebar) kendi
  // yerel bookkeeping'ini tutar; burada tekrar tutmaya gerek yok.
  joinRoom(room: string) {
    this.send({ type: "joinRoom", room });
  }

  leaveRoom(room: string) {
    this.send({ type: "leaveRoom", room });
  }

  // ── Presence ──

  /** :9100 op3'ü Kaynak 1-1 tam parametre setiyle (status+afk+mobile+custom_status) destekler. */
  updatePresence(status: string, afk?: boolean, mobile?: boolean, customStatus?: unknown) {
    this.v2?.updatePresence(status, afk, mobile, customStatus);
  }

  // ── Messaging ──
  // Aşağıdaki relay'ler Discord'da REST-güdümlü (bkz. sendV2 default case) —
  // metodlar KORUNUR (çağıran kod değişmedi) ama wire'a gitmezler.

  broadcastMessage(event: string, data: any) {
    this.send({ type: "broadcast:message", event, data });
  }

  updatePin(chatId: string) {
    this.send({ type: "pin:update", chat_id: chatId });
  }

  updateReaction(chatId: string, messageId: string, reactions: any) {
    this.send({ type: "reaction:update", chat_id: chatId, message_id: messageId, reactions });
  }

  sendDmReaction(targetProfileId: string, chatId: string, messageId: string, reactions: any) {
    this.send({
      type: "dm:reaction-update",
      target_profile_id: targetProfileId,
      chat_id: chatId,
      message_id: messageId,
      reactions,
    });
  }

  // ── Typing ──

  startTyping(channelId: string, userId: string, userName: string, userImage?: string) {
    this.send({
      type: "typing:start",
      channel_id: channelId,
      user_id: userId,
      user_name: userName,
      user_image: userImage,
    });
  }

  stopTyping(channelId: string, userId: string) {
    this.send({ type: "typing:stop", channel_id: channelId, user_id: userId });
  }

  // ── Voice ──
  // NOT: gerçek voice-join akışı features/voice kendi hook'ları üzerinden
  // GatewayV2Socket.updateVoiceState'e (op4) doğrudan gidiyor (bkz. rapor
  // "bağlanış zinciri" bölümü) — bu iki metod yalnız eski çağrı yüzeyini
  // korur (kullanılmıyor; sendV2 "voice:join"/"voice:leave" case'i üzerinden
  // aynı op4'e gider, davranış tutarlı).

  joinVoice(channelId: string, userId: string, userName: string, imageUrl?: string) {
    this.send({ type: "voice:join", channel_id: channelId, user_id: userId, user_name: userName, image_url: imageUrl });
  }

  leaveVoice(channelId: string) {
    this.send({ type: "voice:leave", channel_id: channelId });
  }

  sendIceCandidate(to: string, candidate: any, from: string) {
    this.send({ type: "voice:ice-candidate", to, candidate, from });
  }

  // ── DM Calls ──
  // Adım 16.1: 10 dead method silindi (sendVoiceOffer, sendVoiceAnswer,
  // sendCallInvite, answerCall, declineCall, sendDmOffer, sendDmAnswer,
  // sendDmIce, emitMemberKicked, emitMemberBanned). LiveKit migrasyonundan
  // sonra bu eventler artık gateway'den gönderilmiyor; legacy WebRTC sinyali.

  endCall(callId: string, peerId: string) {
    this.send({ type: "dm:call-end", call_id: callId, peer_id: peerId });
  }

  // ── Invite ──
  // KALAN (backend işi, bu turun kapsamı dışı): Erlang :9100'de karşılığı yok
  // → no-op düşer, alıcı realtime bildirim almaz (bkz. sendV2 başlık notu).

  sendInvite(targetProfileId: string, serverName: string, inviteUrl: string, senderName: string, senderImage?: string) {
    this.send({
      type: "invite:send",
      target_profile_id: targetProfileId,
      server_name: serverName,
      invite_url: inviteUrl,
      sender_name: senderName,
      sender_image: senderImage,
    });
  }
}

// Singleton instance
export const tebhosWs = new TebhosWebSocket();
